import { useState, useEffect, useRef } from "react";
import { FiSearch } from "react-icons/fi";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useApiRequestMutation } from "../../services/apiService";
import ToastrService from "../../services/toastService";
import UserDrawer from "./userDrawer";
import { isSuperAdmin } from "../../services/utils";
import { useNavigate } from "react-router-dom";
import XLSX from "xlsx-js-style";
import { saveAs } from "file-saver";


const UserManagement = () => {

    const [users, setUsers] = useState([]);
    const [search, setSearch] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");

    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const [totalRecords, setTotalRecords] = useState(0);
    const [totalPages, setTotalPages] = useState(1);

    const [errorMsg, setErrorMsg] = useState("");

    const [apiRequest, { isLoading }] = useApiRequestMutation();

    const prevParams = useRef({});

    const [activeUsersCount, setActiveUsersCount] = useState(0)
    const [inActiveUsersCount, setInActiveUsersCount] = useState(0)
    const [unconfirmedUsersCount, setUnconfirmedUsersCount] = useState(0)
    const [deletedUsersCount, setDeletedUsersCount] = useState(0)
    const [allUsersCount, setAllUsersCount] = useState(0)


    const navigate = useNavigate();

    const [statusFilter, setStatusFilter] = useState("All");
    const [isOpen, setIsOpen] = useState(false);

    // download 

    const [showExportModal, setShowExportModal] = useState(false);
    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");
    const [exportLoading, setExportLoading] = useState(false);
    const superAdmin = isSuperAdmin();

    const handleOpenExport = () => {
        setShowExportModal(true);
    };

    const formatDate = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
    };

    const handleExport = async () => {
        try {
            setExportLoading(true);

            const res = await apiRequest({
                url: "/admin/downloadUserList",
                method: "POST",
                data: {
                    startDate: formatDate(fromDate),
                    endDate: formatDate(toDate)
                }
            }).unwrap();

            const usersData = res?.data?.users || [];

            const excelData = usersData.map((user) => {
                const baseData = {
                    "First Name": user.first_name,
                    "Last Name": user.last_name,
                    "Email": user.email,
                    "Employee ID": user.employee_id,
                    "FLEXZ ID": user.flexz_id,
                    "Primary Role Name": user["primaryRole.role_displayname"],
                };

                // Only Admin gets these fields
                if (!superAdmin) {
                    return {
                        ...baseData,
                        // "Department": user["department.dropdown_name"],
                        "Secondary Role Name": user["secondaryRole.role_displayname"],
                    };
                }

                return baseData;
            });
            // Create worksheet
            const worksheet = XLSX.utils.json_to_sheet(excelData);

            // Get range
            const range = XLSX.utils.decode_range(worksheet['!ref']);

            // Header Style
            const headerStyle = {
                font: {
                    bold: true,
                    color: { rgb: "FFFFFF" },
                    sz: 12
                },
                fill: {
                    fgColor: { rgb: "4F81BD" }
                },
                alignment: {
                    horizontal: "center",
                    vertical: "center"
                },
                border: {
                    top: { style: "thin", color: { rgb: "000000" } },
                    bottom: { style: "thin", color: { rgb: "000000" } },
                    left: { style: "thin", color: { rgb: "000000" } },
                    right: { style: "thin", color: { rgb: "000000" } }
                }
            };

            // Table Cell Style
            const cellStyle = {
                border: {
                    top: { style: "thin", color: { rgb: "D3D3D3" } },
                    bottom: { style: "thin", color: { rgb: "D3D3D3" } },
                    left: { style: "thin", color: { rgb: "D3D3D3" } },
                    right: { style: "thin", color: { rgb: "D3D3D3" } }
                },
                alignment: {
                    vertical: "center",
                    horizontal: "left",
                    wrapText: true
                }
            };

            // Apply header style
            for (let col = range.s.c; col <= range.e.c; col++) {
                const cellAddress = XLSX.utils.encode_cell({ r: 0, c: col });

                if (worksheet[cellAddress]) {
                    worksheet[cellAddress].s = headerStyle;
                }
            }

            // Apply cell styles
            for (let row = 1; row <= range.e.r; row++) {
                for (let col = range.s.c; col <= range.e.c; col++) {
                    const cellAddress = XLSX.utils.encode_cell({ r: row, c: col });

                    if (worksheet[cellAddress]) {
                        worksheet[cellAddress].s = cellStyle;
                    }
                }
            }

            // Column widths
            worksheet["!cols"] = superAdmin
                ? [
                    { wch: 20 }, // First Name
                    { wch: 20 }, // Last Name
                    { wch: 35 }, // Email
                    { wch: 20 }, // Employee ID
                    { wch: 20 }, // FLEXZ ID
                    { wch: 30 }  // Primary Role
                ]
                : [
                    { wch: 20 }, // First Name
                    { wch: 20 }, // Last Name
                    { wch: 35 }, // Email
                    { wch: 20 }, // Employee ID
                    { wch: 20 }, // FLEXZ ID
                    { wch: 25 }, // Primary Role
                    // { wch: 25 }, // Department
                    { wch: 30 }, // Secondary Role                   
                ];

            // Create workbook
            const workbook = XLSX.utils.book_new();

            XLSX.utils.book_append_sheet(workbook, worksheet, "Users");

            // Generate Excel file
            const excelBuffer = XLSX.write(workbook, {
                bookType: "xlsx",
                type: "array",
            });

            const file = new Blob([excelBuffer], {
                type: "application/octet-stream",
            });

            saveAs(file, `users_${Date.now()}.xlsx`);

            setShowExportModal(false);
            setFromDate(null);
            setToDate(null);

        } catch (error) {
            console.error("Export failed:", error?.data?.message);

            if (error?.data?.message === 'No data found') {
                setShowExportModal(false);
                setFromDate(null);
                setToDate(null);

                ToastrService.error(error.data.message);
            }
        } finally {
            setExportLoading(false);
        }
    };

    const options = [
        { label: `All (${allUsersCount})`, value: "All" },
        { label: `Active Users (${activeUsersCount})`, value: "Active" },
        { label: `Inactive Users (${inActiveUsersCount})`, value: "Inactive" },
        { label: `Unconfirmed Users (${unconfirmedUsersCount})`, value: "Unconfirmed" },
        { label: `Deleted Users (${deletedUsersCount})`, value: "Deleted" },
    ];

    const UserStatusBadge = ({ status }) => {
        let badgeClass = "";

        switch (status.toLowerCase()) {
            case "active":
                badgeClass = "bg-success"; // green
                break;
            case "inactive":
                badgeClass = "bg-secondary"; // gray
                break;
            case "unconfirmed":
                badgeClass = "bg-warning text-dark"; // yellow
                break;
            case "deleted":
                badgeClass = "bg-danger"; // red
                break;
            default:
                badgeClass = "bg-light text-dark";
        }

        return <span className={`badge ${badgeClass}`}>{status}</span>;
    };

    /* -------------------- SEARCH DEBOUNCE -------------------- */

    useEffect(() => {

        const timer = setTimeout(() => {
            setDebouncedSearch(search);
        }, 500);

        return () => clearTimeout(timer);

    }, [search]);


    /* -------------------- FETCH USERS -------------------- */


    const fetchUsers = async (createUserstatus) => {

        const params = {
            page: currentPage,
            limit: rowsPerPage,
            filter: debouncedSearch,
            status: statusFilter
        };

        // if (JSON.stringify(prevParams.current) === JSON.stringify(params) && !createUserstatus) {
        //     return;
        // }

        // prevParams.current = params;
        const paramsKey = `${currentPage}-${rowsPerPage}-${debouncedSearch}-${statusFilter}`;

        if (prevParams.current === paramsKey && !createUserstatus) {
            return;
        }

        prevParams.current = paramsKey;

        try {

            setErrorMsg("");

            const res = await apiRequest({
                url: "/admin/getUsers",
                method: "POST",
                data: {
                    page: currentPage,
                    limit: rowsPerPage,
                    filter: debouncedSearch.trim(),
                    sortField: "created_at",
                    sortOrder: "DESC",
                    status: statusFilter
                }
            }).unwrap();
            // console.log('resres', res)
            const usersData = res?.data?.users || [];
            const pagination = res?.data?.pagination || {};

            setUsers(usersData);
            setTotalRecords(pagination.total || 0);
            setTotalPages(pagination.totalPages || 1);
            setActiveUsersCount(res.data.counts.active || 0);
            setInActiveUsersCount(res.data.counts.inactive || 0);
            setUnconfirmedUsersCount(res.data.counts.unconfirmed || 0);
            setDeletedUsersCount(res.data.counts.deleted || 0);
            setAllUsersCount(res.data.counts.all || 0);


        } catch (error) {

            console.error("Fetch Users Error:", error);

            setUsers([]);
            setTotalRecords(0);
            setTotalPages(1);

            const message =
                error?.data?.message ||
                error?.message ||
                "Failed to load users";

            setErrorMsg(message);

            // toast.error(message);
            // ToastrService.error(message);

        }
    };
    useEffect(() => {
        fetchUsers();
    }, [currentPage, rowsPerPage, debouncedSearch, statusFilter]);



    const startEntry = totalRecords === 0
        ? 0
        : (currentPage - 1) * rowsPerPage + 1;

    const endEntry = Math.min(currentPage * rowsPerPage, totalRecords);

    /*Drawerr open close*/
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);

    // Open drawer for creating new user
    const handleCreateUser = () => {
        setIsDrawerOpen(true);
    };

    const handleCloseDrawer = () => {
        setIsDrawerOpen(false);
    };
    const handleSave = (userData) => {
        fetchUsers(true);
        // Call API here to save user
        handleCloseDrawer();
    };
    const getPageNumbers = () => {
        const pages = [];

        if (totalPages <= 5) {
            for (let i = 1; i <= totalPages; i++) {
                pages.push(i);
            }
        } else {
            pages.push(1);

            if (currentPage > 3) {
                pages.push("...");
            }

            let start = Math.max(2, currentPage - 1);
            let end = Math.min(totalPages - 1, currentPage + 1);

            for (let i = start; i <= end; i++) {
                pages.push(i);
            }

            if (currentPage < totalPages - 2) {
                pages.push("...");
            }

            pages.push(totalPages);
        }

        return pages;
    };
    const toTitleCase = (text) => {
        if (!text) return "";
        return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
    };
    return (
        <div>

            {/* Header */}

            <div className="page-header">
                <div className="page-header-left d-flex align-items-center">
                    <ul className="breadcrumb mb-0">
                        <li className="breadcrumb-item">User Management</li>
                        <li className="breadcrumb-item active">User</li>
                    </ul>
                </div>
            </div>

            <div className="main-content">

                <div className="row">

                    <div className="p-6">

                        {/* Title */}

                        <div className="d-flex justify-content-between align-items-center mb-3">

                            <div>
                                <h3 className="h4 mb-1 drawer-header">
                                    User Management ({totalRecords})
                                </h3>
                                <p className="mb-0 text-muted">
                                    Manage All Users.
                                </p>
                            </div>

                            {/* <button className="btn btn-primary" >
                                Download
                            </button>
                            
                            <button className="btn btn-primary" onClick={handleCreateUser}>
                                Create User
                            </button> */}


                            {/* Button group */}
                            <div className="d-flex gap-2 flex-wrap justify-content-end">
                                <button className="btn btn-success" onClick={handleOpenExport}>
                                    Download
                                </button>

                                <button className="btn btn-primary" onClick={handleCreateUser}>
                                    Create User
                                </button>
                            </div>

                        </div>


                        <div className="card p-3 mb-4">

                            {/* Toolbar */}
                            <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-2 mb-1">

                                {/* Left Tabs */}
                                <ul className="nav pl-2">
                                    <li className="nav-item">
                                        <a className="nav-link active tab-primary" href="#">
                                            Users
                                        </a>
                                    </li>
                                </ul>

                                {/* Right controls */}
                                <div className="d-flex flex-column flex-sm-row align-items-stretch align-items-sm-center gap-2">

                                    {/* Search Box */}
                                    <div className="search-box">
                                        <FiSearch className="search-icon" />
                                        {/* <input
                                            type="text"
                                            className="custom-search-input form-control search-input"
                                            placeholder="Search"
                                        /> */}
                                        <input
                                            type="text"
                                            className="custom-search-input form-control search-input"
                                            placeholder="Search"
                                            value={search}
                                            onChange={(e) => {
                                                setSearch(e.target.value);
                                                setCurrentPage(1); // reset to first page when searching
                                            }}
                                        />
                                    </div>

                                    {/* User Filter */}
                                    {/* <Dropdown
                                        as={ButtonGroup}
                                        show={isOpen}
                                        onToggle={() => setIsOpen(!isOpen)}
                                        style={{ width: "230px" }} // fixed width
                                    >
                                        <Dropdown.Toggle
                                            variant="outline-primary"
                                            className={`w-100 custom-dropdown-toggle ${isOpen ? 'active-border' : ''}`}
                                        >
                                            {options.find(opt => opt.value === statusFilter)?.label}
                                        </Dropdown.Toggle>

                                        <Dropdown.Menu
                                            className="custom-dropdown-menu"
                                            style={{ maxHeight: "200px", overflowY: "auto" }}
                                        >
                                            {options.map((opt) => (
                                                <Dropdown.Item
                                                    key={opt.value}
                                                    active={statusFilter === opt.value}
                                                    onClick={() => {
                                                        setStatusFilter(opt.value);
                                                        setCurrentPage(1);
                                                        setIsOpen(false);
                                                    }}
                                                    className="custom-dropdown-item"
                                                >
                                                    {opt.label}
                                                </Dropdown.Item>
                                            ))}
                                        </Dropdown.Menu>
                                    </Dropdown> */}
                                    <div
                                        className="d-flex flex-column flex-grow-1 flex-lg-grow-0"
                                        style={{ flex: "0 0 20%", minWidth: "250px" }}
                                    >
                                        <select
                                            className="form-select form-select-sm"
                                            value={statusFilter}
                                            onChange={(e) => {
                                                setStatusFilter(e.target.value);
                                                setCurrentPage(1);
                                            }}
                                        >
                                            {options.map((opt) => (
                                                <option key={opt.value} value={opt.value}>
                                                    {opt.label}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                </div>

                            </div>

                            {/* Table */}

                            <div className="table-responsive">

                                <table className="table table-hover mb-0">

                                    <thead>
                                        <tr>
                                            <th>Employee ID</th>
                                            <th>Full Name</th>
                                            <th>Email Address</th>
                                            <th>FLEXZ ID</th>
                                            <th>Primary Role</th>
                                            {/* <th>Secondary Role</th> */}
                                            <th>User Status</th>
                                        </tr>
                                    </thead>

                                    <tbody>

                                        {isLoading && (
                                            <tr>
                                                <td colSpan="6" className="text-center">
                                                    Loading...
                                                </td>
                                            </tr>
                                        )}

                                        {!isLoading && users.length === 0 && (
                                            <tr>
                                                <td colSpan="6" className="text-center">
                                                    No data found
                                                </td>
                                            </tr>
                                        )}

                                        {!isLoading && users.map((user, index) => (

                                            <tr key={user.id}>

                                                <td className="text-primary employee-link" style={{ cursor: "pointer" }}
                                                    onClick={() =>
                                                        navigate("/user/detail", { state: { userId: user.id } })
                                                    }> {user.employee_id}</td>


                                                <td>
                                                    {toTitleCase(user.first_name)} {toTitleCase(user.last_name)}
                                                </td>

                                                <td>{user.email}</td>

                                                <td>{user.flexz_id}</td>

                                                <td>
                                                    {(() => {
                                                        const value = user?.["primaryRole.role_displayname"] || "";

                                                        const isFirstLetterCapital =
                                                            value.charAt(0) === value.charAt(0).toUpperCase();

                                                        return isFirstLetterCapital ? value : toTitleCase(value);
                                                    })()}
                                                </td>

                                                {/* <td >
                                                    {toTitleCase(user['secondaryRole.role_displayname'])}
                                                </td> */}
                                                <td>
                                                    <UserStatusBadge status={toTitleCase(user.employee_status)} />
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                                {/* Pagination */}

                                <div className="d-flex flex-column flex-md-row justify-content-between align-items-center mt-3 gap-2">

                                    <div className="d-flex align-items-center gap-1 small">

                                        <span>Show</span>

                                        <select
                                            className="form-select form-select-sm page-size-select"
                                            value={rowsPerPage}
                                            onChange={(e) => {
                                                setRowsPerPage(Number(e.target.value));
                                                setCurrentPage(1);
                                            }}
                                        >
                                            <option value={1}>1</option>

                                            <option value={5}>5</option>
                                            <option value={10}>10</option>
                                            <option value={15}>15</option>
                                        </select>

                                        <span>entries</span>

                                    </div>

                                    <div className="small text-muted">
                                        Showing {startEntry} to {endEntry} of {totalRecords} entries
                                    </div>

                                    {/* <ul className="pagination mb-0">

                                        <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                                            <button
                                                className="page-link"
                                                onClick={() => setCurrentPage(currentPage - 1)}
                                            >
                                                Prev
                                            </button>
                                        </li>

                                        <li className="page-item disabled">
                                            <span className="page-link">
                                                Page {currentPage} of {totalPages}
                                            </span>
                                        </li>

                                        <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
                                            <button
                                                className="page-link"
                                                onClick={() => setCurrentPage(currentPage + 1)}
                                            >
                                                Next
                                            </button>
                                        </li>

                                    </ul> */}
                                    <ul className="pagination mb-0">

                                        <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                                            <button
                                                className="page-link"
                                                onClick={() => setCurrentPage(currentPage - 1)}
                                            >
                                                Prev
                                            </button>
                                        </li>

                                        {getPageNumbers().map((page, index) =>
                                            page === "..." ? (
                                                <li key={index} className="page-item disabled">
                                                    <span className="page-link">...</span>
                                                </li>
                                            ) : (
                                                <li
                                                    key={index}
                                                    className={`page-item ${currentPage === page ? "active" : ""}`}
                                                >
                                                    <button
                                                        className="page-link"
                                                        onClick={() => setCurrentPage(page)}
                                                    >
                                                        {page}
                                                    </button>
                                                </li>
                                            )
                                        )}

                                        <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
                                            <button
                                                className="page-link"
                                                onClick={() => setCurrentPage(currentPage + 1)}
                                            >
                                                Next
                                            </button>
                                        </li>

                                    </ul>
                                </div>

                            </div>

                        </div>

                    </div>

                </div>

            </div>


            {isDrawerOpen && (
                <UserDrawer
                    mode="add"
                    roleList={[]} // pass roles from API or state
                    onClose={handleCloseDrawer}
                    onSave={handleSave}
                />
            )}


            {showExportModal && (
                <div className="export-overlay">
                    <div className="export-modal">

                        {/* Header */}
                        <div className="export-header">
                            <h5>Export Users</h5>
                            <button
                                className="export-close"
                                onClick={() => setShowExportModal(false)}
                            >
                                ×
                            </button>
                        </div>

                        {/* Body */}
                        <div className="export-body">

                            {/* NOTE BOX (highlighted) */}
                            <div className="export-note-box">
                                ⚠️ Only the earliest <b>5000 records</b> within the selected date range will be exported.
                            </div>

                            {/* Date Section */}
                            <div className="export-field">
                                <label>Date Range</label>

                                <DatePicker
                                    selectsRange
                                    startDate={fromDate}
                                    endDate={toDate}
                                    onChange={(dates) => {
                                        const [start, end] = dates;
                                        setFromDate(start);
                                        setToDate(end);
                                    }}
                                    placeholderText="Select From - To Date"
                                    className="form-control export-input"
                                    maxDate={new Date()}
                                    dateFormat="yyyy-MM-dd"
                                    showMonthDropdown
                                    showYearDropdown
                                    dropdownMode="select"
                                    isClearable
                                />
                            </div>

                        </div>

                        {/* Footer */}
                        <div className="export-footer">

                            <button
                                className="btn-cancel"
                                onClick={() => {
                                    setShowExportModal(false);
                                    setFromDate(null);
                                    setToDate(null);
                                }}
                            >
                                Cancel
                            </button>

                            <button
                                className="btn-export"
                                onClick={handleExport}
                                disabled={exportLoading || !fromDate || !toDate}
                            >
                                {exportLoading ? "Exporting..." : "Export"}
                            </button>

                        </div>

                    </div>
                </div>
            )}
        </div>
    );
};

export default UserManagement;  